import { audioEngine } from './AudioEngine';
import { tuning } from '@/config/tuning';

/**
 * Soundtrack.ts — the REAL-FILE music layer.
 *
 * M85 (owner ask): real music plays across every menu screen (title / garage /
 * shop / online hub / results…) FIFA-style — one continuous playlist that
 * survives screen hops and resumes after races instead of restarting.
 * M90 (owner ask): that menu playlist is now the OFFICIAL playlist — the
 * English theme anthem opens every fresh session, then the anthem + all nine
 * city race songs rotate in SHUFFLE order (reshuffled per cycle, no
 * back-to-back repeat), each at its loudness-trimmed level.
 *
 * M89 (owner ask): each CITY now has its own real song for racing. The song
 * starts fading in on the tour transition screen (globe flight / World Tour
 * Cup intro) — or the quick-race city splash — and simply CONTINUES into and
 * through the race: one unbroken piece of music from the loading screen to
 * the finish line. A city with no song file races to the ANTHEM instead (the
 * default theme, owner rule) — it keeps playing from the menus and settles to
 * the race-bed level, so song-less cities are a feature state, not an error.
 * The M85 tour-transition quick clip is DORMANT since M89 (the destination
 * city's song owns transitions now) — deck + file kept, say the word to
 * re-route it somewhere.
 *
 * Playlists are DISCOVERED from the asset folders at build time:
 *   src/assets/music/menu/  → the menu playlist (alphabetical; prefix "01 ")
 *   src/assets/music/race/  → per-city race songs, named
 *                             "Dribble Racer <City>[ 2].mp3|m4a" — drop a
 *                             song named after its city and the next build
 *                             wires it to that city's races, zero code
 *                             (several files for one city = that city's
 *                             race playlist, alphabetical).
 *
 * Architecture: THREE persistent <audio> decks (menu + transition + race),
 * each through its own GainNode into the shared musicBus — the MUSIC volume
 * setting and the fanfare duck apply for free, and each deck keeps its own
 * track position naturally. All fades are WebAudio gain ramps: scheduled on
 * the audio thread, they stay smooth even if the main thread is busy building
 * a city. Any file that fails to load falls back down the chain (city song →
 * anthem → synth groove via `onUnavailable`) — never silence, never broken.
 */

const globUrls = (globbed: Record<string, string>): string[] =>
  Object.keys(globbed)
    .sort()
    .map((k) => globbed[k]);

/** The menu playlist (exported for the discovery regression test). */
export const MENU_TRACKS: string[] = globUrls(
  import.meta.glob<string>('/src/assets/music/menu/*', {
    eager: true,
    query: '?url',
    import: 'default',
  }),
);

/** Tour loading/transition clips (dormant since M89; exported for the test). */
export const TRANSITION_TRACKS: string[] = globUrls(
  import.meta.glob<string>('/src/assets/music/transition/*', {
    eager: true,
    query: '?url',
    import: 'default',
  }),
);

// ----- M89: per-city race songs ---------------------------------------------

const RACE_FILE_URLS: Record<string, string> = import.meta.glob<string>(
  '/src/assets/music/race/*',
  { eager: true, query: '?url', import: 'default' },
);

/** File-name spellings accepted for each city (lowercased, punctuation-free).
 *  The owner names files after the city ("Dribble Racer New York.mp3"). */
const CITY_ALIASES: Record<string, string[]> = {
  nyc: ['nyc', 'new york', 'new york city'],
  london: ['london'],
  napoli: ['napoli', 'naples'],
  tokyo: ['tokyo'],
  rio: ['rio', 'rio de janeiro'],
  dakar: ['dakar'],
  lagos: ['lagos'],
  mumbai: ['mumbai'],
  patagonia: ['patagonia'],
};

/** "src/…/Dribble Racer New York.mp3" → "dribble racer new york" (the
 *  normalized basename — also the TRACK_TRIMS key). Pure, tested. */
export function trackKey(path: string): string {
  const base = path.split('/').pop() ?? path;
  return base
    .replace(/\.[a-z0-9]+$/i, '')
    .toLowerCase()
    .replace(/[^a-z0-9 ]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Which city a race-folder file belongs to, from its name. Accepts the
 *  "Dribble Racer <City>" convention (prefix optional) and a trailing
 *  variant number ("Dribble Racer Tokyo 2"). Anything unrecognized returns
 *  null — the discovery test fails loudly on it instead of silently skipping
 *  a misnamed song. Pure, tested. */
export function cityIdForTrackFile(path: string): string | null {
  let key = trackKey(path);
  if (key.startsWith('dribble racer')) key = key.slice('dribble racer'.length).trim();
  key = key.replace(/\s+\d+$/, ''); // "tokyo 2" → "tokyo" (city playlists)
  if (key.length === 0) return null;
  for (const [id, aliases] of Object.entries(CITY_ALIASES)) {
    if (aliases.includes(key)) return id;
  }
  return null;
}

/** Per-FILE gain trims, keyed by trackKey() — measured with ffmpeg loudnorm
 *  (2026-07-22) and set so every race song hits the ANTHEM's ≈ -12 LUFS
 *  reference before raceGain shapes the bed level. A file with no entry
 *  plays at 1.0: master new songs around the anthem's level (≈ -12 LUFS)
 *  and they need no trim; hotter or quieter masters get one number here. */
export const TRACK_TRIMS: Record<string, number> = {
  'dribble racer champion': 1.16, // -13.3 LUFS (M96 — the tour-finale celebration song)
  'dribble racer dakar': 0.56, // -7.0 LUFS, +2.4 dBTP master — 5 dB down (also un-clips the peaks)
  'dribble racer lagos': 1.36, // -14.7 LUFS
  'dribble racer london': 1.33, // -14.5 LUFS
  'dribble racer mumbai': 1.12, // -13.0 LUFS
  'dribble racer napoli': 1.3, // -14.3 LUFS (v2 master, 2026-07-22)
  'dribble racer new york': 1.31, // -14.3 LUFS
  'dribble racer patagonia': 1.31, // -14.4 LUFS
  'dribble racer rio': 1.15, // -13.2 LUFS
  'dribble racer tokyo': 1.25, // -13.9 LUFS
};

export interface RaceTrack {
  url: string;
  trim: number;
}

/** The race folder's discovered SOURCE paths (for the trim-key test). */
export const RACE_FILE_KEYS: string[] = Object.keys(RACE_FILE_URLS).sort();

/** cityId → that city's race playlist (usually one song). Exported for the
 *  discovery regression test. */
export const RACE_TRACKS: Record<string, RaceTrack[]> = {};
/** Race-folder files whose name matched NO city — must stay empty (tested). */
export const UNMAPPED_RACE_FILES: string[] = [];
for (const path of Object.keys(RACE_FILE_URLS).sort()) {
  const id = cityIdForTrackFile(path);
  if (id === null) {
    UNMAPPED_RACE_FILES.push(path);
    continue;
  }
  (RACE_TRACKS[id] ??= []).push({
    url: RACE_FILE_URLS[path],
    trim: TRACK_TRIMS[trackKey(path)] ?? 1,
  });
}

// ----- M96: the tour-finale CELEBRATION song ---------------------------------

/** The owner's "Dribble Racer - Champion" — scores the podium cinematic and
 *  the final-standings page (NOT part of the menu rotation: it's the finale's
 *  own moment). Discovered like every other folder — drop a replacement file
 *  here and the next build wires it, zero code. */
const CELEBRATION_FILE_URLS: Record<string, string> = import.meta.glob<string>(
  '/src/assets/music/celebration/*',
  { eager: true, query: '?url', import: 'default' },
);
/** The celebration folder's SOURCE paths (for the trim-key test). */
export const CELEBRATION_FILE_KEYS: string[] = Object.keys(CELEBRATION_FILE_URLS).sort();
export const CELEBRATION_TRACKS: RaceTrack[] = CELEBRATION_FILE_KEYS.map((path) => ({
  url: CELEBRATION_FILE_URLS[path],
  trim: TRACK_TRIMS[trackKey(path)] ?? 1,
}));

// ----- M90: the menu "official playlist" ------------------------------------

/** Every song the menus rotate through: the anthem folder's tracks first
 *  (index 0 = the English theme anthem — alphabetical, so a "01 " prefix
 *  keeps it leading if more menu tracks land), then every city race song at
 *  its loudness-trimmed level. Exported for the playlist regression test. */
export const MENU_POOL: RaceTrack[] = [
  ...MENU_TRACKS.map((url) => ({ url, trim: 1 })),
  ...Object.values(RACE_TRACKS).flat(),
];

/** A Fisher-Yates permutation of [0..n-1]; if `avoidFirst` would open the
 *  cycle (an immediate repeat of the song that just played), it's swapped
 *  away. Pure with an injected rand — tested. */
export function shuffledOrder(n: number, rand: () => number, avoidFirst = -1): number[] {
  const order = Array.from({ length: n }, (_, i) => i);
  for (let i = n - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [order[i], order[j]] = [order[j], order[i]];
  }
  if (n > 1 && order[0] === avoidFirst) {
    const j = 1 + Math.floor(rand() * (n - 1));
    [order[0], order[j]] = [order[j], order[0]];
  }
  return order;
}

/** A fresh session's play order (owner rule): the English anthem FIRST, then
 *  the whole playlist — city songs included — in shuffle order. Later cycles
 *  reshuffle everything (see advanceMenu). */
const firstSessionOrder = (): number[] =>
  MENU_POOL.length === 0
    ? []
    : [0, ...shuffledOrder(MENU_POOL.length - 1, Math.random).map((i) => i + 1)];

interface Deck {
  el: HTMLAudioElement;
  node: MediaElementAudioSourceNode;
  gain: GainNode;
  /** Bumped on every resume — cancels a pending fade-out pause. */
  gen: number;
  pauseTimer: number | null;
}

export class Soundtrack {
  /** What the soundtrack is playing right now (null = synth fallback). NOTE a
   *  song-less city races on the ANTHEM, so mode stays 'menu' during those
   *  races — `raceCity` says which city the race deck is carrying. */
  private mode: 'menu' | 'transition' | 'race' | 'celebration' | null = null;
  private ctx: AudioContext | null = null;
  private menu: Deck | null = null;
  private trans: Deck | null = null;
  private race: Deck | null = null;
  /** M96: the tour-finale celebration deck (the Champion song). */
  private celeb: Deck | null = null;
  private celebLoaded = false;
  private celebDead = false;
  private celebGapTimer: number | null = null;

  /** M90 playlist state: play order (indices into MENU_POOL) + position.
   *  A fresh session opens on the anthem, then shuffles (owner rule). */
  private menuOrder: number[] = firstSessionOrder();
  private menuPos = 0;
  /** The menu deck needs a (new) src before playing. */
  private menuNeedLoad = true;
  private menuErrors = 0;
  private menuDead = false;
  /** menuGain on menus; raceGain when menu music serves as the race bed. */
  private menuBase = tuning.soundtrack.menuGain;
  private transLoaded = false;
  private transDead = false;
  private hiddenPaused = false;
  /** True once the menu deck has audibly played (gates the landing fade). */
  private started = false;
  /** Delays the next playlist track by trackGapS after one ends. */
  private gapTimer: number | null = null;

  /** M89 race-deck state: which city's song is loaded, where in that city's
   *  playlist we are, and cities whose every file failed (→ anthem instead). */
  private raceCity: string | null = null;
  private raceIdx = 0;
  private raceErrors = 0;
  private raceDead = new Set<string>();
  private raceGapTimer: number | null = null;
  /** One-deck city→city handoff: fade the old song out, THEN swap src. */
  private raceSwapTimer: number | null = null;

  /** Flow hooks this to fall back to the synth groove on total file failure. */
  onUnavailable: (() => void) | null = null;

  /** Start (or seamlessly CONTINUE) the menu playlist at menu level. Returns
   *  false when no playable menu track exists (caller starts the synth groove
   *  instead). Calling this on every menu screen is safe — an already-playing
   *  anthem is left exactly where it is (the owner's "don't restart" rule). */
  playMenu(): boolean {
    return this.startMenuDeck(tuning.soundtrack.menuGain);
  }

  /** M89: the city's own song for a race. Call it as early as the transition
   *  animation / intro splash — the song fades in there and simply KEEPS
   *  PLAYING into and through the race (a same-city re-call while the deck is
   *  rolling never restarts it). No song for this city (or every file failed)
   *  → the ANTHEM is the race music (the default theme, owner rule), eased to
   *  the race-bed level. Returns false only when nothing file-based can play
   *  (caller starts the synth city groove). */
  playRace(cityId: string): boolean {
    const tracks = RACE_TRACKS[cityId];
    if (!tracks || tracks.length === 0 || this.raceDead.has(cityId)) {
      return this.startMenuDeck(tuning.soundtrack.raceGain);
    }
    if (!this.ensure() || !this.race) return false;
    const d = this.race;
    // Same city, already rolling (started on the transition screen, or an
    // applyMood re-run at race start / audio unlock) — continue untouched.
    if (this.mode === 'race' && this.raceCity === cityId && (!d.el.paused || this.hiddenPaused)) {
      this.setLevel(d, this.raceLevel(), 1.0);
      return true;
    }
    this.clearRaceTimers();
    this.clearGap();
    this.stopCelebDeck(0.6);
    if (this.mode === 'menu' && this.menu) this.fadeOut(this.menu, 0.8);
    if (this.mode === 'transition' && this.trans) this.fadeOut(this.trans, 0.3);
    // A DIFFERENT city's song still audibly rolling (tour hop with no menu
    // screen between): one deck, so fade it out first, then swap + fade in.
    const midSong = this.mode === 'race' && this.raceCity !== cityId && !d.el.paused;
    this.mode = 'race';
    this.raceCity = cityId;
    this.raceIdx = 0;
    this.raceErrors = 0;
    if (midSong) {
      this.fadeOut(d, 0.55);
      this.raceSwapTimer = window.setTimeout(() => {
        this.raceSwapTimer = null;
        if (this.mode === 'race' && this.raceCity === cityId) {
          this.startRaceTrack(tuning.soundtrack.raceFadeInS);
        }
      }, 700);
    } else {
      // Fresh entry (quick-race splash / tour transition): from the top.
      this.startRaceTrack(tuning.soundtrack.raceFadeInS);
    }
    return true;
  }

  /** M96: the CHAMPION song — scores the tour-finale podium cinematic AND the
   *  final-standings page (owner drop, "Dribble Racer - Champion"). Starts
   *  from the top each finale; a re-call while it's rolling (the cinematic
   *  hands off to the standings) just CONTINUES — one unbroken piece of music
   *  across the whole celebration. Loops with a breath if the standings
   *  outlast it. No file (or it failed) → the ANTHEM takes the moment at menu
   *  level. Returns false only when nothing file-based can play. */
  playCelebration(): boolean {
    if (CELEBRATION_TRACKS.length === 0 || this.celebDead) {
      return this.startMenuDeck(tuning.soundtrack.menuGain);
    }
    if (!this.ensure() || !this.celeb) return false;
    const d = this.celeb;
    // Already the celebration (cinematic → summary, or an applyMood re-run /
    // audio unlock) — continue exactly where it is.
    if (this.mode === 'celebration' && (!d.el.paused || this.hiddenPaused)) {
      this.setLevel(d, this.celebLevel(), 1.0);
      return true;
    }
    if (this.mode === 'menu' && this.menu) this.fadeOut(this.menu, 0.8);
    if (this.mode === 'transition' && this.trans) this.fadeOut(this.trans, 0.25);
    this.stopRaceDeck(0.6);
    this.clearGap();
    this.clearCelebTimer();
    this.mode = 'celebration';
    if (!this.celebLoaded) {
      d.el.src = CELEBRATION_TRACKS[0].url;
      this.celebLoaded = true;
    } else {
      try {
        d.el.currentTime = 0; // every finale opens the song from the top
      } catch {
        /* not seekable yet — it will simply play from wherever it loaded */
      }
    }
    this.resume(d, this.celebLevel(), 0.8);
    return true;
  }

  /** M102: the title's ⏭ button — jump the menu playlist to its next track
   *  NOW (quick fade instead of the natural end-of-song breath). Only acts
   *  while menu music is the thing playing (on a menu screen); during races /
   *  the celebration it's a no-op. If the deck is mid track-gap (song just
   *  ended, breath pending) the QUEUED next track starts immediately instead
   *  of double-advancing. Returns false when nothing file-based can skip. */
  skipMenuTrack(): boolean {
    if (MENU_POOL.length === 0 || this.menuDead) return false;
    if (!this.ensure() || !this.menu) return false;
    if (this.mode !== 'menu') return false;
    // A pending gapTimer means onMenuEnded already advanced the playlist —
    // skipping then just means "stop breathing, play it".
    const queued = this.gapTimer !== null;
    this.clearGap();
    if (!queued) {
      this.advanceMenu();
      this.fadeOut(this.menu, 0.22);
    }
    this.menuNeedLoad = true;
    this.gapTimer = window.setTimeout(
      () => {
        this.gapTimer = null;
        if (this.mode !== 'menu' || !this.menu) return;
        this.menu.el.src = this.menuEntry().url;
        this.menuNeedLoad = false;
        this.resume(this.menu, this.menuBase * this.menuEntry().trim, tuning.soundtrack.fadeInS);
      },
      queued ? 0 : 280,
    );
    return true;
  }

  /** Play the tour loading/transition clip from the top. DORMANT since M89
   *  (transitions carry the destination city's song now) — kept wired for a
   *  one-line re-route if the owner wants the clip back somewhere. */
  playTransition(): boolean {
    if (TRANSITION_TRACKS.length === 0 || this.transDead) return false;
    if (!this.ensure() || !this.trans) return false;
    if (this.mode === 'menu' && this.menu) this.fadeOut(this.menu, 0.3);
    if (this.mode === 'race' && this.race) this.stopRaceDeck(0.3);
    this.stopCelebDeck(0.3);
    this.mode = 'transition';
    if (!this.transLoaded) {
      this.trans.el.src = TRANSITION_TRACKS[0];
      this.transLoaded = true;
    } else {
      try {
        this.trans.el.currentTime = 0;
      } catch {
        /* not seekable yet — it will simply play from wherever it loaded */
      }
    }
    this.resume(this.trans, tuning.soundtrack.transitionGain, 0.35);
    return true;
  }

  /** The SYNTH race groove is taking over (file fallback): fade whatever file
   *  deck is playing OUT under it — never a hard cut. The menu deck keeps its
   *  position for the post-race resume. */
  releaseToRace(): void {
    if (this.mode === null) return;
    const deck = this.activeDeck();
    this.mode = null;
    this.clearGap();
    this.clearRaceTimers();
    this.clearCelebTimer();
    if (deck) this.fadeOut(deck, tuning.soundtrack.raceFadeS);
  }

  /** Debug/verification handle (?debug=1): what's live right now. */
  get state(): { mode: string | null; raceCity: string | null; track: string; time: number; paused: boolean } {
    const deck = this.activeDeck();
    return {
      mode: this.mode,
      raceCity: this.raceCity,
      track: deck?.el.src.split('/').pop() ?? '',
      time: deck?.el.currentTime ?? 0,
      paused: deck?.el.paused ?? true,
    };
  }

  // ----- Internals ------------------------------------------------------------

  /** The playlist entry the menu deck is (or should be) on. */
  private menuEntry(): RaceTrack {
    return MENU_POOL[this.menuOrder[this.menuPos] ?? 0];
  }

  /** Step the playlist; a finished cycle reshuffles the WHOLE pool (anthem
   *  included), never opening on the song that just played. */
  private advanceMenu(): void {
    this.menuPos++;
    if (this.menuPos >= this.menuOrder.length) {
      const last = this.menuOrder[this.menuOrder.length - 1] ?? -1;
      this.menuOrder = shuffledOrder(MENU_POOL.length, Math.random, last);
      this.menuPos = 0;
    }
  }

  /** Menu deck at an arbitrary base level: menuGain on menu screens, raceGain
   *  when menu music IS the race bed (file-failure fallback). Levels are
   *  base × the current track's loudness trim. An already-playing deck just
   *  GLIDES to the requested level — one continuous piece of music from the
   *  menus into the race and back (the owner's "smooth continued play"). */
  private startMenuDeck(base: number): boolean {
    if (MENU_POOL.length === 0 || this.menuDead) return false;
    if (!this.ensure() || !this.menu) return false;
    this.menuBase = base;
    this.stopRaceDeck(0.6);
    this.stopCelebDeck(0.8);
    if (this.mode === 'transition' && this.trans) this.fadeOut(this.trans, 0.25);
    // Already rolling (or politely paused for a hidden tab) — leave the track
    // exactly where it is: never restart on menu hops. A silently-PAUSED menu
    // deck here means the load-time autostart was blocked by the browser
    // (M85b) — fall through and start it for real now that audio is allowed.
    if (this.mode === 'menu' && (!this.menu.el.paused || this.hiddenPaused)) {
      this.setLevel(this.menu, base * this.menuEntry().trim, 1.2);
      return true;
    }
    this.mode = 'menu';
    if (this.menuNeedLoad) {
      this.menu.el.src = this.menuEntry().url;
      this.menuNeedLoad = false;
    }
    // Resuming a paused deck keeps its position — the song picks up where the
    // player left for the race, and plays through once per listen-through.
    // The session's FIRST start gets the long gentle landing fade (M85b).
    const fade = this.started ? tuning.soundtrack.fadeInS : tuning.soundtrack.landingFadeS;
    this.resume(this.menu, base * this.menuEntry().trim, fade);
    return true;
  }

  /** Load + fade in the race deck's current city/playlist position. */
  private startRaceTrack(fadeS: number): void {
    if (!this.race || !this.raceCity) return;
    const tracks = RACE_TRACKS[this.raceCity];
    if (!tracks || tracks.length === 0) return;
    this.race.el.src = tracks[this.raceIdx % tracks.length].url;
    this.resume(this.race, this.raceLevel(), fadeS);
  }

  /** The race deck's target gain: the bed level scaled by the current file's
   *  measured loudness trim (so every city's song lands at the same level). */
  private raceLevel(): number {
    const tracks = this.raceCity ? RACE_TRACKS[this.raceCity] : undefined;
    const trim = tracks?.[this.raceIdx % Math.max(1, tracks.length)]?.trim ?? 1;
    return tuning.soundtrack.raceGain * trim;
  }

  private stopRaceDeck(fadeS: number): void {
    this.clearRaceTimers();
    if (this.race && !this.race.el.paused) this.fadeOut(this.race, fadeS);
  }

  private clearRaceTimers(): void {
    if (this.raceGapTimer !== null) {
      window.clearTimeout(this.raceGapTimer);
      this.raceGapTimer = null;
    }
    if (this.raceSwapTimer !== null) {
      window.clearTimeout(this.raceSwapTimer);
      this.raceSwapTimer = null;
    }
  }

  private activeDeck(): Deck | null {
    return this.mode === 'menu'
      ? this.menu
      : this.mode === 'transition'
        ? this.trans
        : this.mode === 'race'
          ? this.race
          : this.mode === 'celebration'
            ? this.celeb
            : null;
  }

  /** The celebration deck's target gain (celebration level × the file's trim). */
  private celebLevel(): number {
    return tuning.soundtrack.celebrationGain * (CELEBRATION_TRACKS[0]?.trim ?? 1);
  }

  private stopCelebDeck(fadeS: number): void {
    this.clearCelebTimer();
    if (this.celeb && !this.celeb.el.paused) this.fadeOut(this.celeb, fadeS);
  }

  private clearCelebTimer(): void {
    if (this.celebGapTimer !== null) {
      window.clearTimeout(this.celebGapTimer);
      this.celebGapTimer = null;
    }
  }

  /** The champion song finished while the standings are still up: breathe,
   *  then loop it from the top (the celebration owns the page). */
  private onCelebEnded(): void {
    if (this.mode !== 'celebration') return;
    this.clearCelebTimer();
    this.celebGapTimer = window.setTimeout(() => {
      this.celebGapTimer = null;
      if (this.mode !== 'celebration' || !this.celeb) return;
      try {
        this.celeb.el.currentTime = 0;
      } catch {
        /* not seekable — replays from wherever it can */
      }
      this.resume(this.celeb, this.celebLevel(), tuning.soundtrack.fadeInS);
    }, tuning.soundtrack.trackGapS * 1000);
  }

  /** The champion file failed: the anthem takes the celebration instead. */
  private onCelebError(): void {
    this.celebDead = true;
    if (this.mode === 'celebration') {
      this.mode = null;
      if (!this.startMenuDeck(tuning.soundtrack.menuGain)) this.onUnavailable?.();
    }
  }

  /** Build the decks once per AudioContext (needs the first-gesture unlock). */
  private ensure(): boolean {
    const ctx = audioEngine.context;
    const bus = audioEngine.musicBus;
    if (!ctx || !bus) return false;
    if (this.ctx === ctx && this.menu && this.trans && this.race && this.celeb) return true;
    this.menu = this.makeDeck(
      ctx,
      bus,
      () => this.onMenuEnded(),
      () => this.onMenuError(),
    );
    // The landing fade applies until the anthem has AUDIBLY started once —
    // 'playing' (not our own play() call, which autoplay can silently block)
    // is the truth signal.
    this.menu.el.addEventListener('playing', () => {
      this.started = true;
    });
    this.trans = this.makeDeck(
      ctx,
      bus,
      null, // clip end during a long load = intentional silence until the race
      () => this.onTransError(),
    );
    this.race = this.makeDeck(
      ctx,
      bus,
      () => this.onRaceEnded(),
      () => this.onRaceError(),
    );
    this.celeb = this.makeDeck(
      ctx,
      bus,
      () => this.onCelebEnded(),
      () => this.onCelebError(),
    );
    if (this.ctx === null && typeof document !== 'undefined') {
      // A hidden tab keeps <audio> playing (unlike the throttled synth
      // sequencer) — pause politely, resume when the player comes back.
      document.addEventListener('visibilitychange', () => this.onVisibility());
    }
    this.ctx = ctx;
    return true;
  }

  private makeDeck(
    ctx: AudioContext,
    bus: GainNode,
    onEnded: (() => void) | null,
    onError: () => void,
  ): Deck {
    const el = new Audio();
    el.preload = 'auto';
    const node = ctx.createMediaElementSource(el);
    const gain = ctx.createGain();
    gain.gain.value = 0;
    node.connect(gain).connect(bus);
    if (onEnded) el.addEventListener('ended', onEnded);
    el.addEventListener('error', onError);
    return { el, node, gain, gen: 0, pauseTimer: null };
  }

  private resume(deck: Deck, level: number, fadeS: number): void {
    deck.gen++;
    if (deck.pauseTimer !== null) {
      window.clearTimeout(deck.pauseTimer);
      deck.pauseTimer = null;
    }
    const t = this.ctx?.currentTime ?? 0;
    deck.gain.gain.cancelScheduledValues(t);
    // Starting from silence must FADE from silence: a blocked load-time
    // play() leaves the gain parked at full level on a paused (silent) deck,
    // and ramping "full → full" on the real start would land as a jolt —
    // exactly what the fade exists to prevent. Snapping a paused deck to
    // zero is inaudible by definition.
    if (deck.el.paused) deck.gain.gain.setValueAtTime(0.0001, t);
    deck.gain.gain.setTargetAtTime(level, t, Math.max(0.05, fadeS / 3));
    void deck.el.play().catch(() => {
      /* autoplay-blocked or mid-load — the next gesture/mood change retries */
    });
  }

  /** Glide an already-playing deck to a new level (menu ↔ race bed). */
  private setLevel(deck: Deck, level: number, fadeS: number): void {
    const t = this.ctx?.currentTime ?? 0;
    deck.gain.gain.cancelScheduledValues(t);
    deck.gain.gain.setTargetAtTime(level, t, Math.max(0.05, fadeS / 3));
  }

  /** Fade a deck to silence, THEN pause it (a raw pause() clicks). */
  private fadeOut(deck: Deck, fadeS: number): void {
    const gen = ++deck.gen;
    const t = this.ctx?.currentTime ?? 0;
    deck.gain.gain.cancelScheduledValues(t);
    deck.gain.gain.setTargetAtTime(0.0001, t, Math.max(0.05, fadeS / 3));
    if (deck.pauseTimer !== null) window.clearTimeout(deck.pauseTimer);
    deck.pauseTimer = window.setTimeout(
      () => {
        deck.pauseTimer = null;
        if (deck.gen === gen) deck.el.pause();
      },
      fadeS * 1000 + 80,
    );
  }

  /** A menu track finished: breathe, then the playlist's next song (M90 —
   *  shuffle rotation through the anthem + every city song). menuBase keeps
   *  the level family (menu vs race bed) across the track change. */
  private onMenuEnded(): void {
    this.advanceMenu();
    this.menuNeedLoad = true;
    if (this.mode !== 'menu') return; // raced off mid-song — playMenu reloads later
    this.clearGap();
    this.gapTimer = window.setTimeout(() => {
      this.gapTimer = null;
      if (this.mode !== 'menu' || !this.menu) return;
      this.menu.el.src = this.menuEntry().url;
      this.menuNeedLoad = false;
      this.resume(this.menu, this.menuBase * this.menuEntry().trim, tuning.soundtrack.fadeInS);
    }, tuning.soundtrack.trackGapS * 1000);
  }

  /** The race song finished mid-race: breathe, then loop the city's playlist
   *  (usually the same song — long races replay it, FIFA-broadcast style). */
  private onRaceEnded(): void {
    if (this.mode !== 'race' || !this.raceCity) return;
    const tracks = RACE_TRACKS[this.raceCity] ?? [];
    if (tracks.length === 0) return;
    this.raceIdx = (this.raceIdx + 1) % tracks.length;
    this.clearRaceTimers();
    this.raceGapTimer = window.setTimeout(() => {
      this.raceGapTimer = null;
      if (this.mode !== 'race' || !this.race) return;
      this.startRaceTrack(tuning.soundtrack.fadeInS);
    }, tuning.soundtrack.trackGapS * 1000);
  }

  private clearGap(): void {
    if (this.gapTimer !== null) {
      window.clearTimeout(this.gapTimer);
      this.gapTimer = null;
    }
  }

  private onMenuError(): void {
    this.menuErrors++;
    if (this.menuErrors >= MENU_POOL.length) {
      // Every playlist track failed — hand the menus back to the synth groove.
      this.menuDead = true;
      if (this.mode === 'menu') {
        this.mode = null;
        this.onUnavailable?.();
      }
      return;
    }
    // Skip the broken file, try the playlist's next song.
    this.advanceMenu();
    this.menuNeedLoad = true;
    if (this.mode === 'menu' && this.menu) {
      this.menu.el.src = this.menuEntry().url;
      this.menuNeedLoad = false;
      this.resume(this.menu, this.menuBase * this.menuEntry().trim, tuning.soundtrack.fadeInS);
    }
  }

  /** A race file failed: try the city's next file, else mark the city dead
   *  and fall back down the chain (anthem → synth groove). */
  private onRaceError(): void {
    const city = this.raceCity;
    if (city === null) return;
    this.raceErrors++;
    const tracks = RACE_TRACKS[city] ?? [];
    if (this.raceErrors >= tracks.length) {
      this.raceDead.add(city);
      if (this.mode === 'race') {
        this.mode = null;
        if (!this.startMenuDeck(tuning.soundtrack.raceGain)) this.onUnavailable?.();
      }
      return;
    }
    this.raceIdx = (this.raceIdx + 1) % tracks.length;
    if (this.mode === 'race') this.startRaceTrack(tuning.soundtrack.fadeInS);
  }

  private onTransError(): void {
    this.transDead = true;
    if (this.mode === 'transition') {
      this.mode = null;
      if (!this.playMenu()) this.onUnavailable?.();
    }
  }

  private onVisibility(): void {
    if (typeof document === 'undefined') return;
    const deck = this.activeDeck();
    if (document.hidden) {
      if (deck && !deck.el.paused) {
        deck.el.pause();
        this.hiddenPaused = true;
      }
    } else if (this.hiddenPaused) {
      this.hiddenPaused = false;
      if (deck && this.mode !== null) void deck.el.play().catch(() => {});
    }
  }
}

/** The game-wide soundtrack singleton (silent until the first-gesture unlock). */
export const soundtrack = new Soundtrack();
